<template>
    <div class="session-duration-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: 'Chart View' }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: 'Table View' }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: 'Download' }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="middle">
                <label>Aggregate as:</label>
                <a-radio-group size="small" v-model="aggregationType">
                    <a-radio-button value="avg"> Avg</a-radio-button>
                    <a-radio-button value="total"> Total</a-radio-button>
                </a-radio-group>
            </div>
            <div class="right">
                <label>View unit as:</label>
                <a-radio-group size="small" v-model="unit">
                    <a-radio-button value="seconds"> Second</a-radio-button>
                    <a-radio-button value="minutes"> Minute</a-radio-button>
                    <a-radio-button value="hours"> Hour</a-radio-button>
                </a-radio-group>
            </div>
        </div>
        <TableView v-if="tableMode" :cols="getCols()" :rows="getRows()" />
        <StackedAreaChart
            :title="aggregationType === 'avg' ? 'Average Session Duration' : 'Total Session Duration'"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-else
        />
        <div class="stats">
            <StatCard title="Total Duration" :value="stats.TotalDuration" />
            <StatCard title="Average Duration" :value="stats.AverageDuration" />
            <StatCard title="Max Duration" :value="stats.MaxDuration" />
            <StatCard title="Min Duration" :value="stats.MinDuration" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import { expertToExcel } from "../../../utils/utils";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";

export default Vue.extend({
    name: "OverallSessionDurationChart",
    components: { IconButton, StatCard, StackedAreaChart, TableView },
    props: {
        /**
         * [
         *     {
         *         "date": "2023-09-25T00:00:00+00:00",
         *         "items": [
         *             {
         *                 "createdDate": "2023-09-25T22:22:27.765Z",
         *                 "duration": 1202,
         *                 "successMatchCount": 0,
         *                 "failedMatchCount": 0
         *             },
         *         ]
         *     }
         * ]
         */
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: ["Duration"],
            stats: this.getStats(),
            aggregationType: "total",
            unit: "hours",
            tableMode: false,
        };
    },
    methods: {
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getStats() {
            const { buckets } = this.data;
            if (buckets && buckets.length > 0) {
                const items = _.flatMap(buckets, (bucket) => bucket.items).map((x) => x.duration);
                if (items.length > 0) {
                    const TotalDuration = _.chain(items).sum().value();
                    const AverageDuration = parseInt(_.mean(items).toFixed(1));
                    const MaxDuration = parseInt(_.max(items).toFixed(1));
                    const MinDuration = parseInt(_.min(items).toFixed(1));

                    return {
                        TotalDuration: this.humanizeDuration(TotalDuration),
                        AverageDuration: this.humanizeDuration(AverageDuration),
                        MaxDuration: this.humanizeDuration(MaxDuration),
                        MinDuration: this.humanizeDuration(MinDuration),
                    };
                }
            }
            return {
                TotalDuration: "0",
                AverageDuration: "0",
                MaxDuration: "0",
                MinDuration: "0",
            };
        },

        getXAxisData() {
            const { buckets, timeSeriesType } = this.data;

            return buckets.map((item) => {
                if (timeSeriesType === "daily") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hourly") {
                    return moment(item.date).format("HH:mm");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;

            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => {
                    const unitRation = {
                        seconds: 1,
                        minutes: 60,
                        hours: 60 * 60,
                    }[this.unit];
                    const value =
                        this.aggregationType === "avg"
                            ? _.chain(bucket.items)
                                  .map((x) => x.duration / unitRation)
                                  .mean()
                                  .value()
                            : _.chain(bucket.items)
                                  .map((x) => x.duration / unitRation)
                                  .sum()
                                  .value();
                    return value || 0;
                });
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: "rgb(255, 0, 135)",
                            },
                            {
                                offset: 1,
                                color: "rgb(135, 0, 157)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        getCols() {
            return [
                {
                    title: "Date",
                    dataIndex: "date",
                    key: "date",
                    width: 100,
                    fixed: "left",
                },
                {
                    title: "Total Duration",
                    dataIndex: "totalDuration",
                    key: "totalDuration",
                    width: 100,
                },
            ];
        },
        getRows() {
            const { buckets } = this.data;
            return buckets.map((bucket) => {
                const items = bucket.items.map((x) => x.duration);
                const TotalDuration = _.chain(items).sum().value();
                return {
                    date: moment(bucket.date).format("DD/MM/YY"),
                    totalDuration: this.humanizeDuration(TotalDuration),
                };
            });
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;

            const data = buckets.map((bucket) => {
                const items = bucket.items.map((x) => x.duration);
                const totalDuration = _.chain(items).sum().value();
                const averageDuration = parseInt(_.mean(items).toFixed(1));
                const maxDuration = parseInt(_.max(items).toFixed(1));
                const minDuration = parseInt(_.min(items).toFixed(1));
                return {
                    date: moment(bucket.date).format("DD/MM/YY"),
                    "Toplam Süre": this.humanizeDuration(totalDuration),
                    "Ortalama süre": this.humanizeDuration(averageDuration),
                    "Maksimum Süre": this.humanizeDuration(maxDuration),
                    "Minimum Süre": this.humanizeDuration(minDuration),
                };
            });

            expertToExcel(data, `session_duration_timeseries.xlsx`, "Sheet 1");
        },
    },
    watch: {
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        unit(val) {
            this.stats = this.getStats();
        },
    },
});
</script>

<style scoped lang="scss">
.session-duration-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
